import React, { useState } from 'react';
import { Plus, Minus, Calculator, Download } from 'lucide-react';
import { generateQuote } from '../mock';
import { useData } from '../hooks/useData';
import { backendService } from '../lib/api';
import AddressInput from './AddressInput';

const PricingWizard = () => {
  const [properties, setProperties] = useState([]);
  const [currentProperty, setCurrentProperty] = useState({
    address: '',
    addressData: null,
    items: {}
  });
  const [quote, setQuote] = useState(null);
  const [showQuote, setShowQuote] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { linenTypes, pricingConfig, isLoading, error } = useData();

  const handleAddressChange = (addressData) => {
    setCurrentProperty(prev => ({
      ...prev,
      address: typeof addressData === 'string' ? addressData : addressData.formattedAddress,
      addressData: typeof addressData === 'object' ? addressData : null
    }));
  };

  const updateItemQuantity = (itemId, quantity) => {
    setCurrentProperty(prev => ({
      ...prev,
      items: {
        ...prev.items,
        [itemId]: Math.max(0, quantity)
      }
    }));
  };

  const addProperty = () => {
    if (currentProperty.address && Object.keys(currentProperty.items).length > 0) {
      const newProperty = {
        ...currentProperty,
        id: Date.now().toString(),
        // Store the full address data if available
        coordinates: currentProperty.addressData?.coordinates || null,
        addressComponents: currentProperty.addressData?.components || {},
        placeId: currentProperty.addressData?.placeId || null
      };
      setProperties(prev => [...prev, newProperty]);
      setCurrentProperty({ address: '', addressData: null, items: {} });
    }
  };

  const removeProperty = (propertyId) => {
    setProperties(prev => {
      const updatedProperties = prev.filter(p => p.id !== propertyId);
      
      // If all properties are removed, reset the quote
      if (updatedProperties.length === 0) {
        setQuote(null);
        setShowQuote(false);
        setCustomerInfo({ name: '', email: '', phone: '' });
      }
      
      return updatedProperties;
    });
  };

  const generateFullQuote = () => {
    if (properties.length > 0) {
      const quoteData = generateQuote(properties, pricingConfig, linenTypes);
      setQuote(quoteData);
      setShowQuote(true);
    }
  };

  const handleCustomerInfoChange = (field, value) => {
    setCustomerInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitQuote = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const quoteData = {
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        customer_phone: customerInfo.phone,
        quote: {
          monthlyRental: quote.monthlyRental,
          launderingPerVisit: quote.launderingPerVisit,
          deliveryChargesPerVisit: quote.deliveryChargesPerVisit,
          totalPropertiesCount: quote.totalPropertiesCount,
          itemBreakdown: quote.itemBreakdown,
        },
        properties: properties,
      };

      const response = await backendService.submitQuote(quoteData);
      
      if (response.success) {
        alert(response.message);
        // Reset form and hide quote
        setCustomerInfo({ name: '', email: '', phone: '' });
        setShowQuote(false);
        setProperties([]);
      } else {
        throw new Error(response.message || 'Failed to submit quote');
      }
      
    } catch (error) {
      console.error('Error submitting quote:', error);
      alert('Error submitting quote. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const groupedItems = linenTypes.reduce((groups, item) => {
    if (!groups[item.category]) {
      groups[item.category] = [];
    }
    groups[item.category].push(item);
    return groups;
  }, {});

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading pricing data...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Data</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-8 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="display-medium text-gray-900 mb-4">
              Get Your Custom Quote
            </h1>
            <p className="body-large text-gray-600 max-w-2xl mx-auto">
              Configure your linen rental needs for each property. Add multiple addresses 
              and specify quantities for each linen type.
            </p>
          </div>

          {/* Property Configuration */}
          <div className="bg-white border border-gray-200 rounded-lg p-8 mb-8">
            <h2 className="heading-2 text-gray-900 mb-6">Add Property</h2>
            
            {/* Address Input */}
            <div className="mb-6">
              <AddressInput
                value={currentProperty.address}
                onChange={handleAddressChange}
                placeholder="Enter property address or business name"
              />
            </div>

            {/* Linen Selection */}
            <div className="space-y-6">
              {Object.entries(groupedItems).map(([category, items]) => (
                <div key={category} className="space-y-4">
                  <h3 className="heading-4 text-gray-900 capitalize border-b border-gray-200 pb-2">
                    {category}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((item) => {
                      const pricing = pricingConfig[item.id];
                      const quantity = currentProperty.items[item.id] || 0;
                      
                      return (
                        <div key={item.id} className="marketplace-card border border-gray-200 p-6">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h4 className="marketplace-card-title">{item.name}</h4>
                              <p className="text-sm text-gray-600">
                                ${pricing.rentalPrice}/month each
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <button
                                onClick={() => updateItemQuantity(item.id, quantity - 1)}
                                className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                                disabled={quantity === 0}
                              >
                                <Minus className="w-4 h-4" />
                              </button>

                              <input
                                type="number"
                                value={quantity}
                                onChange={(e) => updateItemQuantity(item.id, parseInt(e.target.value) || 0)}
                                onFocus={(e) => {
                                  if (quantity === 0) {
                                    e.target.value = '';
                                  }
                                }}
                                onBlur={(e) => {
                                  if (e.target.value === '') {
                                    updateItemQuantity(item.id, 0);
                                  }
                                }}
                                className="w-16 text-center font-medium border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                min="0"
                              />

                              <button
                                onClick={() => updateItemQuantity(item.id, quantity + 1)}
                                className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>

                            {quantity > 0 && (
                              <span className="text-sm font-medium text-gray-900">
                                ${(pricing.rentalPrice * quantity).toFixed(2)}/month
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end mt-6 gap-3">
              <button
                onClick={() => {
                  if (currentProperty.address && Object.keys(currentProperty.items).length > 0) {
                    // Always add the current property to the properties list first
                    const newProperty = {
                      ...currentProperty,
                      id: Date.now().toString(),
                      coordinates: currentProperty.addressData?.coordinates || null,
                      addressComponents: currentProperty.addressData?.components || {},
                      placeId: currentProperty.addressData?.placeId || null
                    };
                    
                    // Add to properties list
                    const updatedProperties = [...properties, newProperty];
                    setProperties(updatedProperties);
                    
                    // Generate quote for all properties (including the newly added one)
                    const quoteData = generateQuote(updatedProperties, pricingConfig, linenTypes);
                    setQuote(quoteData);
                    setShowQuote(true);
                    
                    // Clear the current property form
                    setCurrentProperty({ address: '', addressData: null, items: {} });
                  }
                }}
                disabled={properties.length === 0 && (!currentProperty.address || Object.keys(currentProperty.items).length === 0)}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Calculator className="w-4 h-4" />
                Generate Quote
              </button>
              <button
                onClick={addProperty}
                disabled={!currentProperty.address || Object.keys(currentProperty.items).length === 0}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Property
              </button>
            </div>
          </div>

          {/* Added Properties */}
          {properties.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <h2 className="heading-2 text-gray-900 mb-4">Your Properties ({properties.length})</h2>
              <div className="space-y-4">
                {properties.map((property) => (
                  <div key={property.id} className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-medium text-gray-900">{property.address}</h3>
                      <button
                        onClick={() => removeProperty(property.id)}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(property.items).map(([itemId, quantity]) => {
                        const item = linenTypes.find(i => i.id === itemId);
                        return (
                          <span key={itemId} className="text-sm bg-gray-100 px-2 py-1 rounded">
                            {quantity}x {item?.name}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Hidden second Generate Quote button - removed to avoid confusion */}
            </div>
          )}

          {/* Quote Display */}
          {showQuote && quote && (
            <div className="bg-white border-2 border-red-100 rounded-lg p-8">
              <div className="flex justify-between items-start mb-6">
                <h2 className="heading-2 text-gray-900">Your Quote Summary</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-600 mb-1">Monthly Rental</h3>
                  <p className="heading-2 text-gray-900">${quote.monthlyRental.toFixed(2)}</p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-600 mb-1">Laundering Per Visit</h3>
                  <p className="heading-2 text-gray-900">${quote.launderingPerVisit.toFixed(2)}</p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-600 mb-1">Delivery Charges Per Visit</h3>
                  <p className="heading-2 text-gray-900">${quote.deliveryChargesPerVisit.toFixed(2)}</p>
                </div>
              </div>

              {/* Customer Information Form */}
              <div className="border-t border-gray-200 pt-6 mb-6">
                <h3 className="heading-3 text-gray-900 mb-4">Contact Information</h3>
                <form id="quote-form" onSubmit={handleSubmitQuote} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        value={customerInfo.name}
                        onChange={(e) => handleCustomerInfoChange('name', e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="Enter your full name"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={customerInfo.email}
                        onChange={(e) => handleCustomerInfoChange('email', e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="Enter your email"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        value={customerInfo.phone}
                        onChange={(e) => handleCustomerInfoChange('phone', e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="Enter your phone number"
                      />
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <strong>Next Steps:</strong> After submitting your quote request, our team will review your requirements 
                      and contact you within 24 hours to discuss the details and finalize your linen rental agreement.
                    </p>
                  </div>
                </form>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <p className="text-sm text-gray-600 mb-4">
                  Quote generated for {quote.totalPropertiesCount} {quote.totalPropertiesCount === 1 ? 'property' : 'properties'} 
                  on {new Date(quote.generatedAt).toLocaleDateString()}
                </p>
                
                <div className="flex gap-4">
                  <button 
                    type="submit"
                    form="quote-form"
                    disabled={isSubmitting || !customerInfo.name || !customerInfo.email || !customerInfo.phone}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Submitting...
                      </>
                    ) : (
                      'Submit Quote Request'
                    )}
                  </button>
                  <button 
                    onClick={() => setShowQuote(false)}
                    className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Modify Quote
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PricingWizard;